import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';
import { Role } from '../../types/auth';
import { showToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface RoleShiftDefault {
    id: string;
    roleId: string;
    defaultStartTime: string;
    defaultEndTime: string;
    workDays: string[];
    isAllWeek: boolean;
    role: {
        id: string;
        name: string;
        description?: string;
    };
}

const WEEKDAYS = [
    { value: 'MONDAY', label: 'Monday' },
    { value: 'TUESDAY', label: 'Tuesday' },
    { value: 'WEDNESDAY', label: 'Wednesday' },
    { value: 'THURSDAY', label: 'Thursday' },
    { value: 'FRIDAY', label: 'Friday' },
    { value: 'SATURDAY', label: 'Saturday' },
    { value: 'SUNDAY', label: 'Sunday' },
];

const RoleShiftDefaults: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [defaults, setDefaults] = useState<RoleShiftDefault[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingRole, setEditingRole] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        defaultStartTime: '09:00',
        defaultEndTime: '17:00',
        workDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
        isAllWeek: false,
    });
    const [pendingDeleteRoleId, setPendingDeleteRoleId] = useState<string | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [rolesRes, defaultsRes] = await Promise.all([
                apiClient.get('/roles'),
                apiClient.get('/role-shift-defaults'),
            ]);
            setRoles(rolesRes.data.data);
            setDefaults(defaultsRes.data.data);
        } catch (err: any) {
            setError('Failed to load data: ' + (err.response?.data?.error?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (roleId: string) => {
        const existing = defaults.find(d => d.roleId === roleId);
        if (existing) {
            setFormData({
                defaultStartTime: existing.defaultStartTime,
                defaultEndTime: existing.defaultEndTime,
                workDays: existing.workDays,
                isAllWeek: existing.isAllWeek,
            });
        } else {
            // Reset to defaults
            setFormData({
                defaultStartTime: '09:00',
                defaultEndTime: '17:00',
                workDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
                isAllWeek: false,
            });
        }
        setEditingRole(roleId);
        setError('');
    };

    const handleSave = async () => {
        if (!editingRole) return;

        try {
            await apiClient.put(`/role-shift-defaults/${editingRole}`, formData);
            await fetchData();
            setEditingRole(null);
            setError('');
        } catch (err: any) {
            setError('Failed to save: ' + (err.response?.data?.error?.message || err.message));
        }
    };

    const handleDelete = async (roleId: string) => {
        try {
            await apiClient.delete(`/role-shift-defaults/${roleId}`);
            setPendingDeleteRoleId(null);
            showToast('Shift defaults deleted', 'success');
            await fetchData();
        } catch (err: any) {
            setError('Failed to delete: ' + (err.response?.data?.error?.message || err.message));
        }
    };

    const toggleWorkDay = (day: string) => {
        setFormData(prev => ({
            ...prev,
            workDays: prev.workDays.includes(day)
                ? prev.workDays.filter(d => d !== day)
                : [...prev.workDays, day],
        }));
    };

    const getRoleDefault = (roleId: string) => {
        return defaults.find(d => d.roleId === roleId);
    };

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <>
        <div className="p-8 max-w-6xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Role Shift Defaults</h1>
            <p className="text-gray-600 mb-6">
                Configure default shift times for each role. These defaults will be used during roster generation.
            </p>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Default Times
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Work Days
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {roles.map(role => {
                            const roleDefault = getRoleDefault(role.id);
                            const isEditing = editingRole === role.id;

                            return (
                                <tr key={role.id} className={isEditing ? 'bg-blue-50' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">{role.name}</div>
                                            {role.description && (
                                                <div className="text-xs text-gray-500">{role.description}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {isEditing ? (
                                            <div className="flex gap-2 items-center">
                                                <input
                                                    type="time"
                                                    value={formData.defaultStartTime}
                                                    onChange={e => setFormData({ ...formData, defaultStartTime: e.target.value })}
                                                    className="border rounded px-2 py-1 text-sm"
                                                />
                                                <span>-</span>
                                                <input
                                                    type="time"
                                                    value={formData.defaultEndTime}
                                                    onChange={e => setFormData({ ...formData, defaultEndTime: e.target.value })}
                                                    className="border rounded px-2 py-1 text-sm"
                                                />
                                            </div>
                                        ) : roleDefault ? (
                                            <span className="text-sm text-gray-700">
                                                {roleDefault.defaultStartTime} - {roleDefault.defaultEndTime}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-gray-400 italic">Not configured</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {isEditing ? (
                                            <div>
                                                <label className="flex items-center gap-2 mb-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.isAllWeek}
                                                        onChange={e => setFormData({ ...formData, isAllWeek: e.target.checked })}
                                                        className="rounded"
                                                    />
                                                    <span className="text-sm">All Week</span>
                                                </label>
                                                {!formData.isAllWeek && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {WEEKDAYS.map(day => (
                                                            <label key={day.value} className="flex items-center gap-1 text-xs">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.workDays.includes(day.value)}
                                                                    onChange={() => toggleWorkDay(day.value)}
                                                                    className="rounded"
                                                                />
                                                                {day.label.slice(0, 3)}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : roleDefault ? (
                                            <span className="text-sm text-gray-700">
                                                {roleDefault.isAllWeek
                                                    ? 'All Week'
                                                    : roleDefault.workDays.map(d => d.slice(0, 3)).join(', ')}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-gray-400 italic">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {isEditing ? (
                                            <div className="flex gap-2 justify-end">
                                                <button
                                                    onClick={handleSave}
                                                    className="text-green-600 hover:text-green-900"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingRole(null)}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-3 justify-end">
                                                <button
                                                    onClick={() => handleEdit(role.id)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    {roleDefault ? 'Edit' : 'Configure'}
                                                </button>
                                                {roleDefault && (
                                                    <button
                                                        onClick={() => setPendingDeleteRoleId(role.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>

            <ConfirmDialog
                open={!!pendingDeleteRoleId}
                title="Delete Shift Defaults"
                message="Are you sure you want to delete the shift defaults for this role?"
                confirmLabel="Delete"
                variant="danger"
                onConfirm={() => pendingDeleteRoleId && handleDelete(pendingDeleteRoleId)}
                onCancel={() => setPendingDeleteRoleId(null)}
            />
        </>
    );
};

export default RoleShiftDefaults;
